import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import Alert from '@mui/material/Alert/Alert';
import Box from '@mui/material/Box/Box';
import IconButton from '@mui/material/IconButton/IconButton';
import Paper from '@mui/material/Paper/Paper';
import Stack from '@mui/material/Stack/Stack';
import TextField from '@mui/material/TextField/TextField';
import Typography from '@mui/material/Typography/Typography';
import { blue } from '@mui/material/colors';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import IconDisplay from '@/shared/components/utilities/IconDisplay';

import TileType from '../../components/Tile/types/tile.type';

type AddTileDialogProps = {
  tiles: TileType[];
  selectedTiles: TileType[];
  errors?: string[];
  onClose: () => void;
  onAddTile: (tile: TileType) => void;
  onOpenTileConfigForm?: (tile: TileType) => void;
};

const AddTileDialog: FC<AddTileDialogProps> = ({
  tiles,
  selectedTiles,
  errors,
  onClose,
  onAddTile,
  onOpenTileConfigForm,
}) => {
  const { t } = useTranslation(['checkout', 'common']);

  const [filteredTiles, setFilteredTiles] = useState(tiles);

  if (errors && errors.length) {
    return (
      <ModalNotification
        size='sm'
        fullWidth
        open
        onClose={onClose}
        dialogProps={{
          title: t('checkout:addTileToPOSScreen'),
          content: (
            <Stack sx={{ py: 1 }}>
              {errors.map((error, index) => (
                <Alert key={index} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ),
          actions: [
            {
              label: t('common:close'),
              onClick: onClose,
            },
          ],
        }}
      />
    );
  }

  return (
    <ModalNotification
      size='sm'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: (
          <Stack sx={{ mx: -1 }}>{t('checkout:addTileToPOSScreen')}</Stack>
        ),
        content: (
          <Stack sx={{ py: 1, mx: -3 }}>
            <Box>
              <TextField
                variant='standard'
                placeholder='Type to search'
                fullWidth
                onChange={(e) => {
                  const searchResults = tiles.filter((item) =>
                    new RegExp(e.target.value, 'ig').test(item.title),
                  );
                  setFilteredTiles(searchResults);
                }}
                sx={{
                  mb: 2,
                  px: 2,
                }}
              />
            </Box>

            {filteredTiles.map((tile) => {
              const selectedItem = selectedTiles.some(
                (item) => item.id === tile.id,
              );

              return (
                <Stack
                  key={tile.id}
                  onClick={() => onAddTile(tile)}
                  sx={{
                    'flexDirection': 'row',
                    'gap': 2,
                    'alignItems': 'center',
                    'width': '100%',
                    'py': 2,
                    'px': 3,
                    'borderRadius': 2,
                    'cursor': 'pointer',
                    'backgroundColor': selectedItem ? blue[50] : 'transparent',

                    ':hover': {
                      backgroundColor: blue[100],
                    },
                  }}
                >
                  <Paper
                    sx={{
                      p: 1,
                      backgroundColor: tile.styles?.backgroundColor,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <IconDisplay
                      iconName={tile.icon}
                      sx={{ color: tile.styles?.color, width: 30, height: 30 }}
                    />
                  </Paper>
                  <Stack>
                    <Typography variant='body1'>{tile.title}</Typography>
                    <Typography
                      variant='body1'
                      sx={{ color: 'text.secondary' }}
                    >
                      {tile.description}
                    </Typography>
                  </Stack>

                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenTileConfigForm?.(tile);
                    }}
                    sx={{ ml: 'auto' }}
                  >
                    <TuneOutlinedIcon />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        ),
        actions: [
          {
            variant: 'contained',
            fullWidth: true,
            label: t('common:close'),
            onClick: onClose,
          },
        ],
      }}
    />
  );
};

export default AddTileDialog;
